// stores/userProfileStore.ts
import { create } from 'zustand';
import apiClient from '../apiSetup/axiosInterceptor';

interface Agency {
  id: string;
  agency_code: string;
  name: string;
  description: string | null;
  state: string;
  city: string;
  pincode: string;
  created_at: string | null;
  updated_at: string | null;
}

interface UserProfile {
  id: string;
  email: string | null;
  role: string | null;
  name: string | null;
  gender: string | null;
  address: string | null;
  contact_number: string | null;
  agency_id: string | null;
  agency_code: string | null;
  agency: Agency | null;
  created_at: string | null;
  updated_at: string | null;
}

interface ApiResponse {
  success: boolean;
  data: UserProfile;
  message?: string;
}

interface UpdateProfilePayload {
  name?: string | null;
  gender?: string | null;
  address?: string | null;
  contact_number?: string | null;
}

interface UserProfileState {
  profile: UserProfile | null;
  loading: boolean;
  error: string | null;
  fetchProfile: () => Promise<void>;
  updateProfile: (payload: UpdateProfilePayload) => Promise<boolean>;
  clearError: () => void;
}

export const userProfileStore = create<UserProfileState>((set) => ({
  profile: null,
  loading: false,
  error: null,

  clearError: () => set({ error: null }),

  fetchProfile: async () => {
    set({ loading: true, error: null });
    try {
      const response = await apiClient.get<ApiResponse>('/v1/profile');
      console.log(JSON.stringify(response.data, null, 2));

      set({ profile: response.data.data, loading: false });
    } catch (err: any) {
      const message =
        err.response?.data?.error ||
        err.response?.data?.message ||
        err.message ||
        'Failed to load user profile';
      set({ error: message, loading: false });
    }
  },

  updateProfile: async (payload: UpdateProfilePayload) => {
    set({ loading: true, error: null });
    try {
      const body: Record<string, string | null> = {};
      if (payload.name !== undefined) body.name = payload.name;
      if (payload.gender !== undefined) body.gender = payload.gender;
      if (payload.address !== undefined) body.address = payload.address;
      if (payload.contact_number !== undefined) {
        body.contact_number = payload.contact_number;
      }

      if (Object.keys(body).length === 0) {
        set({ error: 'No fields provided to update.', loading: false });
        return false;
      }

      const response = await apiClient.put<ApiResponse>('/v1/profile', body);
      set({ profile: response.data.data, loading: false });
      return true;
    } catch (err: any) {
      const message =
        err.response?.data?.error ||
        err.response?.data?.message ||
        err.message ||
        'Failed to update profile';
      set({ error: message, loading: false });
      return false;
    }
  },
}));